import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";

import {
  FORCE_LOGIN_QUERY_PARAM,
  FORCE_LOGIN_QUERY_VALUE,
  isNonProductionNavbarColorFromEnv,
  localStorageHelpers,
  Log,
  OauthApi,
  RoutePaths,
  sessionStorageHelpers,
  verifyHttpOnlySessionActive,
  type IOauthConsentRequestDto,
} from "@ryde/shared";

import {
  OAUTH_CONSENT_FORCED_LOGIN_REQUEST_ID_KEY,
  OAUTH_CONSENT_REQUEST_ID_KEY,
} from "./oauth-consent.constants";
import type { TConsentPageState } from "./oauth-consent.models";
import {
  isOauthOwnedReturnTo,
  resolveForcedLoginStep,
  runConsentApiAction,
} from "./oauth-consent.utils";

/**
 * State machine behind the OAuth consent screen (RYDE-7833): session probe → request fetch →
 * approve/deny → redirect back to the OAuth client. Unauthenticated (or not-fresh-enough)
 * sessions bounce through the login page via `returnTo` and come back — including across the
 * Google/Microsoft redirect round-trips (the request id is mirrored to sessionStorage).
 */

export const useOauthConsent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Same switch as the login page, so the shared title renders in the same colors.
  const isNonProductionLoginColors = useMemo(
    () => isNonProductionNavbarColorFromEnv(location.search || location.hash),
    [location.search, location.hash],
  );

  const urlRequestId = searchParams.get("request_id");
  const requestId = useMemo(() => {
    if (urlRequestId) {
      sessionStorageHelpers.setItem(OAUTH_CONSENT_REQUEST_ID_KEY, urlRequestId);
      return urlRequestId;
    }
    return sessionStorageHelpers.getItem<string>(OAUTH_CONSENT_REQUEST_ID_KEY);
  }, [urlRequestId]);

  const [pageState, setPageState] = useState<TConsentPageState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [consentRequest, setConsentRequest] =
    useState<IOauthConsentRequestDto | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToLogin = useEvent((shouldForceLogin: boolean): void => {
    localStorageHelpers.setItem(
      "returnTo",
      `${RoutePaths.OAUTH_CONSENT}?request_id=${requestId}`,
    );
    navigate(
      shouldForceLogin
        ? `${RoutePaths.LOGIN}?${FORCE_LOGIN_QUERY_PARAM}=${FORCE_LOGIN_QUERY_VALUE}`
        : RoutePaths.LOGIN,
    );
  });

  // The flow is over (client redirect or terminal error): nothing consent-owned may outlive it.
  const clearConsentStorage = useEvent((): void => {
    sessionStorageHelpers.setItem(OAUTH_CONSENT_REQUEST_ID_KEY, null);
    sessionStorageHelpers.setItem(
      OAUTH_CONSENT_FORCED_LOGIN_REQUEST_ID_KEY,
      null,
    );
  });

  // Drop the OAuth-owned returnTo once the flow is over — a stale entry would redirect a LATER
  // login back to a consumed/expired consent request. Only ours: an unrelated login flow's
  // destination is never touched (see isOauthOwnedReturnTo).
  const clearOauthReturnTo = useEvent((): void => {
    if (isOauthOwnedReturnTo(localStorageHelpers.getItem<string>("returnTo"))) {
      localStorageHelpers.setItem("returnTo", null);
    }
  });

  const failWith = useEvent((message: string): void => {
    clearOauthReturnTo();
    clearConsentStorage();
    setErrorMessage(message);
    setPageState("error");
  });

  /**
   * approve said the session is not fresh enough. Bounce through a forced login once; if that was
   * already done for this request the IdP cannot prove freshness, and looping would never end.
   */
  const handleFreshLoginRequired = useEvent(
    (currentRequestId: string): void => {
      const step = resolveForcedLoginStep({
        attemptedRequestId: sessionStorageHelpers.getItem<string>(
          OAUTH_CONSENT_FORCED_LOGIN_REQUEST_ID_KEY,
        ),
        requestId: currentRequestId,
      });
      if (step === "stop") {
        failWith(t("oauthConsent.freshLoginUnavailable"));
        return;
      }

      sessionStorageHelpers.setItem(
        OAUTH_CONSENT_FORCED_LOGIN_REQUEST_ID_KEY,
        currentRequestId,
      );
      goToLogin(true);
    },
  );

  useEffect(() => {
    const bootstrap = async (): Promise<void> => {
      if (!requestId) {
        failWith(t("oauthConsent.requestInvalid"));
        return;
      }

      const isSessionActive = await verifyHttpOnlySessionActive();
      if (!isSessionActive) {
        goToLogin(false);
        return;
      }

      const result = await OauthApi.getConsentRequest(requestId);
      if (!result?.success || !result.data) {
        failWith(t("oauthConsent.requestInvalid"));
        return;
      }

      if (!result.data.eligibleCompanies.length) {
        failWith(t("oauthConsent.noEligibleCompany"));
        return;
      }

      setConsentRequest(result.data);
      setSelectedCompanyId(result.data.eligibleCompanies[0].companyId);
      setPageState("ready");
    };

    bootstrap().catch(error => {
      Log.warn("OAuth consent bootstrap failed", error);
      failWith(t("oauthConsent.requestInvalid"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const redirectToClient = useEvent((redirectTo: string): void => {
    clearOauthReturnTo();
    clearConsentStorage();
    window.location.href = redirectTo;
  });

  const handleApprove = useEvent(async (): Promise<void> => {
    if (!requestId || !selectedCompanyId) {
      return;
    }
    setIsSubmitting(true);
    try {
      // runConsentApiAction absorbs any transport failure into an { kind: "error" } action, so the
      // page always stays inside the state machine — never an unhandled rejection (RYDE-7833).
      const action = await runConsentApiAction(() =>
        OauthApi.approveConsent(requestId, selectedCompanyId),
      );

      if (action.kind === "redirect") {
        setPageState("redirecting");
        redirectToClient(action.redirectTo);
        return;
      }
      if (action.kind === "forceLogin") {
        handleFreshLoginRequired(requestId);
        return;
      }

      failWith(t("oauthConsent.requestInvalid"));
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleDeny = useEvent(async (): Promise<void> => {
    if (!requestId) {
      return;
    }
    setIsSubmitting(true);
    try {
      const action = await runConsentApiAction(() =>
        OauthApi.denyConsent(requestId),
      );

      if (action.kind === "redirect") {
        setPageState("denied");
        redirectToClient(action.redirectTo);
        return;
      }

      failWith(t("oauthConsent.requestInvalid"));
    } finally {
      setIsSubmitting(false);
    }
  });

  const selectedCompany = consentRequest?.eligibleCompanies.find(
    company => company.companyId === selectedCompanyId,
  );

  return {
    pageState,
    errorMessage,
    consentRequest,
    selectedCompanyId,
    setSelectedCompanyId,
    isSubmitting,
    shouldShowFreshLoginNotice: Boolean(
      consentRequest?.promptLogin || selectedCompany?.forceLogin,
    ),
    scopes: (consentRequest?.scope ?? "").split(" "),
    isNonProductionLoginColors,
    handleApprove,
    handleDeny,
  };
};
